import { Box, Button, Flex, Grid, Input } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";

import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconTimeDuration15 } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  repositoryMdm,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { formatDateTime } from "../../../common/FormatDate/FormatDate";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { _localization } from "../../../config/location";
import { ModelMessageLog } from "../../../model/ModelMessageLog";
import { useLocation } from "react-router-dom";

type ColumnPinningType = { left?: string[]; right?: string[] };

interface SearchCriteria {
  keySearch?: string;
  FromDate?: Date | string;
  ToDate?: Date | string;
}

const HistorySMSBH = () => {
  // Khởi tạo component InventoryReportList
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [visible, { toggle, close, open }] = useDisclosure(false);

  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [data, setData] = useState<ModelMessageLog[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";

  const form = useForm<SearchCriteria>({
    mode: "uncontrolled",
    initialValues:
      _searchJson && checkedPinSearch
        ? {
            keySearch: _searchJson?.keySearch,
            FromDate: _searchJson?.FromDate,
            ToDate: _searchJson?.ToDate,
          }
        : {},
  });

  const getColumnPinningStorage = (): ColumnPinningType => {
    const stored = localStorage.getItem("columnPinningHistorySMSBH");
    return stored
      ? JSON.parse(stored)
      : {
          left: ["STT"],
          right: [],
        };
  };

  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage()
  );

  useEffect(() => {
    localStorage.setItem(
      "columnPinningHistorySMSBH",
      JSON.stringify(columnPinning)
    );
  }, [columnPinning]);

  const columns = React.useMemo<MRT_ColumnDef<ModelMessageLog>[]>(
    () => [
      {
        accessorKey: "STT",
        header: "STT",
        size: 100,
        Cell: ({ row }) => <>{row.index + 1}</>,
      },
      {
        accessorKey: "originalDocumentCode",
        header: "Mã chứng từ",
      },
      {
        accessorKey: "customerName",
        header: "Tên khách hàng",
      },
      {
        accessorKey: "customerPhone",
        header: "Số điện thoại",
      },
      {
        accessorKey: "messageType",
        header: "Loại tin",
        Cell: ({ renderedCellValue }) => (
          <span style={{ fontWeight: "bold" }}>{renderedCellValue}</span>
        ),
      },
      {
        accessorKey: "messageContent",
        header: "Nội dung tin nhắn",
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        Cell: ({ renderedCellValue }) => {
          let color = "black";
          if (renderedCellValue === "SENT") color = "green";
          if (renderedCellValue === "FAILED") color = "red";
          return (
            <span style={{ color: color, fontWeight: "bold" }}>
              {renderedCellValue}
            </span>
          );
        },
      },
      {
        accessorKey: "createdDate",
        header: "Ngày tạo",
        Cell: ({ renderedCellValue }) => (
          <>{formatDateTime(renderedCellValue as string)}</>
        ),
      },
      {
        accessorKey: "sentTime",
        header: "Thời gian gửi",
        Cell: ({ renderedCellValue }) => (
          <>{formatDateTime(renderedCellValue as string)}</>
        ),
      },
      {
        accessorKey: "createdBy",
        header: "Người tạo",
      },
      {
        accessorKey: "errorMessage",
        header: "Lỗi (nếu có)",
        Cell: ({ renderedCellValue }) => (
          <span style={{ color: "red" }}>{renderedCellValue}</span>
        ),
      },
    ],
    [],
  );

  // Hàm lấy dữ liệu
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;
    const search = form.getValues();
    if (search.keySearch) {
      url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
    }
    if (search.ToDate) {
      url += `&ToDate=${search.ToDate}`;
    }
    if (search.FromDate) {
      url += `&FromDate=${search.FromDate}`;
    }
    try {
      const response = await repositoryMdm.get(
        `/api/v1/TblMessageHistory/get-list${url}`,
      );
      if (response && response.success) {
        let result = response.data as ModelMessageLog[];
        setData(result);
        setRowCount(response.totalCount);
        table.resetRowSelection();
      } else {
        setData([]);
        setRowCount(response.totalCount);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({ ...form.getValues() })
      );
    }
  };

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    rowCount,
    enableColumnFilters: false,
    enableSorting: false,
    enableColumnActions: true,
    // manualPagination: true,
    enableStickyFooter: true,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
      columnPinning,
    },
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    onPaginationChange: setPagination,
    onColumnPinningChange: (newState: any) => {
      setColumnPinning(newState);
    },
    positionToolbarAlertBanner: "bottom",
    initialState: {
      showColumnFilters: false,
      columnPinning: { ...columnPinning },
      columnVisibility: { id: false },
      density: "xs",
    },
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    // getRowId: (row) => row.departmentId,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,

    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });
  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (165 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);
  useEffect(() => {
    fetchData();
  }, [pagination]);

  return (
    <Box>
      <LoadingOverlayCustom visible={visible} />
      <div ref={headerRef} style={{ width: "100%", marginBottom: "10px" }}>
        <Flex justify={"space-between"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          {/* <Button
                        leftSection={
                            <IconFileExport style={{ width: rem(14), height: rem(14) }} />
                        }
                    // onClick={() => exportDataExcel()}
                    >
                        Export Excel
                    </Button> */}
        </Flex>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Input
              placeholder="Nhập tìm kiếm"
              key={form.key("keySearch")}
              type="text"
              leftSection={<IconSearch size={"20"} color="#15aabf" />}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await fetchData();
                }
              }}
              {...form.getInputProps("keySearch")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              key={form.key("FromDate")}
              {...form.getInputProps("FromDate")}
              value={
                form.getValues().FromDate
                  ? dayjs(form.getValues().FromDate).toDate()
                  : null // Nếu không có giá trị, đặt null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "FromDate",
                  date ? dayjs(date).format("YYYY-MM-DDTHH:mm:ss") : "",
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              key={form.key("ToDate")}
              {...form.getInputProps("ToDate")}
              value={
                form.getValues().ToDate
                  ? dayjs(form.getValues().ToDate).toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "ToDate",
                  date ? dayjs(date).format("YYYY-MM-DDTHH:mm:ss") : "",
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Button
              color="blue"
              variant="outline"
              onClick={async () => {
                await fetchData();
              }}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <MantineReactTable table={table} />
    </Box>
  );
};

export default HistorySMSBH;
